import React, { useState, useEffect } from 'react';
import { Sparkles } from 'lucide-react';

const RARITY_COLORS = {
  common: '#9CA3AF',
  uncommon: '#93C5FD',
  rare: '#3B82F6',
  legendary: '#A855F7',
  masterpiece: '#D4AF37',
};
const RARITY_LABELS = {
  common: { en: 'Common', ru: 'Обычная', zh: '普通' },
  uncommon: { en: 'Uncommon', ru: 'Необычная', zh: '非凡' },
  rare: { en: 'Rare', ru: 'Редкая', zh: '稀有' },
  legendary: { en: 'Legendary', ru: 'Легендарная', zh: '传奇' },
  masterpiece: { en: 'Masterpiece', ru: 'Шедевр', zh: '杰作' },
};

const CardRevealModal = ({ card, onComplete, language = 'en' }) => {
  const [stage, setStage] = useState('appearing'); // appearing, hidden, revealed, done, fading

  useEffect(() => {
    const timer = setTimeout(() => {
      setStage('hidden');
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  const handleCardClick = () => {
    if (stage === 'hidden') {
      setStage('revealed');
    }
  };

  const handleClaim = () => {
    if (stage === 'revealed') {
      setStage('done');
      // Short pause to show checkmark, then start fading
      setTimeout(() => {
        setStage('fading');
      }, 600);
      // After fade completes, call onComplete
      setTimeout(() => {
        onComplete();
      }, 1200);
    }
  };

  const getText = (key) => {
    const texts = {
      new_card: {
        en: 'New Painting!',
        ru: 'Новая картина!',
        zh: '新画作！'
      },
      tap_to_reveal: {
        en: 'Tap to reveal',
        ru: 'Нажмите, чтобы открыть',
        zh: '点击打开'
      },
      add_to_collection: {
        en: 'Add to Collection',
        ru: 'Добавить в коллекцию',
        zh: '添加到收藏'
      },
      added: {
        en: 'Added!',
        ru: 'Добавлено!',
        zh: '已添加！'
      },
      hidden_hint: {
        en: 'Tap "Add to Collection" to continue',
        ru: 'Нажмите «Добавить в коллекцию»',
        zh: '点击"添加到收藏"继续'
      }
    };
    return texts[key]?.[language] || texts[key]?.['en'] || key;
  };

  return (
    <div
      className="fixed inset-0 bg-black/95 z-50 flex flex-col items-center justify-center px-6"
      style={{
        transition: 'opacity 0.6s ease-out',
        opacity: stage === 'fading' ? 0 : 1,
      }}
    >
      {/* Background particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(15)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-[#D4AF37] rounded-full animate-float"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              opacity: 0.3 + Math.random() * 0.4,
            }}
          />
        ))}
      </div>

      {/* Title - animates on reveal */}
      <div className="text-center mb-6 relative z-10">
        <div className="relative overflow-hidden" style={{ minHeight: '2rem' }}>
          {/* "New Painting!" - fades out */}
          <h1
            className="font-heading text-2xl transition-all duration-700"
            style={{
              opacity: stage === 'appearing' || stage === 'hidden' ? 1 : 0,
              transform: stage === 'appearing' || stage === 'hidden' ? 'translateY(0)' : 'translateY(-100%)',
              position: stage === 'appearing' || stage === 'hidden' ? 'relative' : 'absolute',
              width: '100%',
              color: '#D4AF37',
            }}
          >
            {getText('new_card')}
          </h1>
          {/* Rarity label - fades in */}
          <h1
            className="font-heading text-2xl font-bold transition-all duration-700"
            style={{
              opacity: stage === 'revealed' || stage === 'done' ? 1 : 0,
              transform: stage === 'revealed' || stage === 'done' ? 'translateY(0)' : 'translateY(100%)',
              color: card ? (RARITY_COLORS[card.rarity] || '#9CA3AF') : '#D4AF37',
              textShadow: card ? `0 0 20px ${RARITY_COLORS[card.rarity] || '#9CA3AF'}66` : 'none',
            }}
          >
            {card ? (RARITY_LABELS[card.rarity]?.[language] || RARITY_LABELS[card.rarity]?.en || card.rarity) : ''}
          </h1>
        </div>
        <div className="relative overflow-hidden mt-0.5" style={{ minHeight: '1.25rem' }}>
          <p
            className="text-sm text-[#F5F5F7] font-medium transition-all duration-700 delay-100"
            style={{
              opacity: stage === 'revealed' || stage === 'done' ? 1 : 0,
              transform: stage === 'revealed' || stage === 'done' ? 'translateY(0)' : 'translateY(100%)',
            }}
          >
            {card?.title || ''}
          </p>
        </div>
      </div>

      {/* Card Container */}
      <div 
        className="relative z-10"
        style={{ perspective: '1000px' }}
      >
        <div
          onClick={handleCardClick}
          className={`w-56 h-80 cursor-pointer transition-all duration-700 ${
            stage === 'appearing' ? 'scale-0 opacity-0' : 'scale-100 opacity-100'
          }`}
          style={{
            transformStyle: 'preserve-3d',
            transform: stage === 'revealed' || stage === 'done' 
              ? 'rotateY(180deg)' 
              : 'rotateY(0deg)',
          }}
        >
          {/* Card Back (face down) */}
          <div
            className={`absolute inset-0 w-full h-full rounded-2xl overflow-hidden ${
              stage === 'hidden' ? 'animate-pulse-gold' : ''
            }`}
            style={{ backfaceVisibility: 'hidden' }}
          >
            <div className="w-full h-full bg-gradient-to-br from-[#1a1a2e] to-[#0C0C14] border-2 border-[#D4AF37]/50 rounded-2xl flex flex-col items-center justify-center">
              <div className="w-20 h-20 mb-4 relative">
                <div className="absolute inset-0 border-2 border-[#D4AF37] rounded-full animate-spin" style={{ animationDuration: '8s' }} />
                <div className="absolute inset-3 border-2 border-[#D4AF37]/50 rounded-full animate-spin" style={{ animationDuration: '6s', animationDirection: 'reverse' }} />
                <div className="absolute inset-6 bg-[#D4AF37] rounded-full flex items-center justify-center">
                  <span className="text-xl">🎨</span>
                </div>
              </div>
              <p className="text-[#D4AF37] font-heading text-lg">PrivateArt</p>
            </div>
          </div>

          {/* Card Front (revealed) - full image */}
          <div
            className="absolute inset-0 w-full h-full rounded-2xl overflow-hidden"
            style={{ 
              backfaceVisibility: 'hidden',
              transform: 'rotateY(180deg)'
            }}
          >
            {card && (
              <div className="w-full h-full border border-[#D4AF37]/30 rounded-2xl overflow-hidden">
                <img
                  src={card.image_url}
                  alt={card.title || 'Card'}
                  className="w-full h-full object-cover"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tap hint for hidden state */}
      {stage === 'hidden' && (
        <p className="mt-6 text-[#A1A1AA] text-sm animate-pulse relative z-10">
          {getText('tap_to_reveal')}
        </p>
      )}

      {/* Add to Collection Button */}
      {(stage === 'revealed' || stage === 'done') && (
        <div className="mt-6 relative z-10">
          <button
            onClick={handleClaim}
            disabled={stage === 'done'}
            data-testid="claim-card-btn"
            className={`px-6 py-3 rounded-xl font-medium text-base transition-all flex items-center gap-2 ${
              stage === 'done'
                ? 'bg-[#10B981] text-white'
                : 'btn-gold animate-pulse-gold'
            }`}
          >
            {stage === 'done' ? (
              <>
                <span>✓</span>
                <span>{getText('added')}</span>
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5" />
                <span>{getText('add_to_collection')}</span>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default CardRevealModal;
